import API_URLS from '../../../config/apiConfig';
import type {
  CreateFeatureFlagRequest,
  FeatureFlagApiClientApplication,
  FeatureFlagAdminResponse,
  FeatureFlagStoresResponse,
  FeatureFlagUsersResponse,
  ReplaceFeatureFlagStoresRequest,
  ReplaceFeatureFlagUsersRequest,
  UpdateFeatureFlagRequest,
} from '../../../types/features/featureFlags';
import { apiGet, apiPost, apiPut } from '../../../utils/apiUtils';
import { ErrorHandler } from '../../../utils/errorHandler';

class FeatureFlagAdminService {
  async getFeatureFlags(clientApplication: FeatureFlagApiClientApplication): Promise<FeatureFlagAdminResponse[]> {
    try {
      const searchParams = new URLSearchParams({ client_application: clientApplication });
      const response = await apiGet(`${API_URLS.ADMIN_FEATURE_FLAGS}?${searchParams.toString()}`);
      return Array.isArray(response) ? response as FeatureFlagAdminResponse[] : [];
    } catch (error) {
      const message = ErrorHandler.handleApiError(error, 'Failed to load feature flags', false);
      throw new Error(message);
    }
  }

  async createFeatureFlag(payload: CreateFeatureFlagRequest): Promise<FeatureFlagAdminResponse> {
    try {
      const response = await apiPost(API_URLS.ADMIN_FEATURE_FLAGS, payload);
      return response as FeatureFlagAdminResponse;
    } catch (error) {
      const message = ErrorHandler.handleApiError(error, 'Failed to create feature flag', false);
      throw new Error(message);
    }
  }

  async updateFeatureFlag(featureFlagId: string, payload: UpdateFeatureFlagRequest): Promise<FeatureFlagAdminResponse> {
    try {
      const response = await apiPut(API_URLS.ADMIN_FEATURE_FLAG(featureFlagId), payload);
      return response as FeatureFlagAdminResponse;
    } catch (error) {
      const message = ErrorHandler.handleApiError(error, 'Failed to update feature flag', false);
      throw new Error(message);
    }
  }

  async getFeatureFlagStoreIds(featureFlagId: string): Promise<string[]> {
    try {
      const response = await apiGet(API_URLS.ADMIN_FEATURE_FLAG_STORES(featureFlagId));
      const featureStoresResponse = response as FeatureFlagStoresResponse;
      return Array.isArray(featureStoresResponse.store_ids) ? featureStoresResponse.store_ids : [];
    } catch (error) {
      const message = ErrorHandler.handleApiError(error, 'Failed to load assigned stores', false);
      throw new Error(message);
    }
  }

  async getFeatureFlagUserIds(featureFlagId: string): Promise<string[]> {
    try {
      const response = await apiGet(API_URLS.ADMIN_FEATURE_FLAG_USERS(featureFlagId));
      const featureUsersResponse = response as FeatureFlagUsersResponse;
      return Array.isArray(featureUsersResponse.user_ids) ? featureUsersResponse.user_ids : [];
    } catch (error) {
      const message = ErrorHandler.handleApiError(error, 'Failed to load assigned users', false);
      throw new Error(message);
    }
  }

  async replaceFeatureFlagStores(featureFlagId: string, payload: ReplaceFeatureFlagStoresRequest): Promise<void> {
    try {
      await apiPut(API_URLS.ADMIN_FEATURE_FLAG_STORES(featureFlagId), payload);
    } catch (error) {
      const message = ErrorHandler.handleApiError(error, 'Failed to save assigned stores', false);
      throw new Error(message);
    }
  }

  async replaceFeatureFlagUsers(featureFlagId: string, payload: ReplaceFeatureFlagUsersRequest): Promise<void> {
    try {
      await apiPut(API_URLS.ADMIN_FEATURE_FLAG_USERS(featureFlagId), payload);
    } catch (error) {
      const message = ErrorHandler.handleApiError(error, 'Failed to save assigned users', false);
      throw new Error(message);
    }
  }
}

const featureFlagAdminService = new FeatureFlagAdminService();

export default featureFlagAdminService;
